import React from 'react'
import { CheckCircle, X, AlertTriangle, Trash2 } from 'lucide-react'
import { Button } from '../ui/Button'

interface CompletionConfirmationDialogProps {
  isOpen: boolean
  onConfirm: () => void
  onCancel: () => void
}

export const CompletionConfirmationDialog: React.FC<CompletionConfirmationDialogProps> = ({
  isOpen,
  onConfirm,
  onCancel
}) => {
  console.log('🔍 CompletionConfirmationDialog render - isOpen:', isOpen)
  
  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-50 animate-in fade-in duration-200"
        onClick={onCancel}
      />
      
      {/* Dialog */}
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-md animate-in zoom-in-95 duration-200">
        <div className="bg-white rounded-lg shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between p-6 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">
                Complete Setup?
              </h2>
            </div>
            <button
              onClick={onCancel}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Content */}
          <div className="px-6 pb-4">
            <p className="text-gray-600 mb-4">
              You're about to finalize your strategic intelligence configuration. This will:
            </p>
            
            <div className="space-y-3 mb-4">
              <div className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                <div className="text-sm text-gray-700">
                  Activate your intelligence monitoring system
                </div>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                <div className="text-sm text-gray-700">
                  Begin processing your first intelligence report
                </div>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                <div className="text-sm text-gray-700">
                  Clear this setup session for future use
                </div>
              </div>
            </div>
            
            {/* Warning about clearing session */}
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
                <div className="text-sm text-amber-800">
                  <strong>Note:</strong> Completing setup will clear your configuration session. 
                  If you need to create another intelligence profile later, you'll start fresh.
                </div>
              </div>
            </div>
          </div>
          
          {/* Actions */}
          <div className="flex items-center justify-end gap-3 p-6 pt-2">
            <Button
              variant="outline"
              onClick={onCancel}
              className="px-4 py-2"
            >
              Review Setup
            </Button>
            <Button
              onClick={onConfirm}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white"
            >
              Complete Setup
            </Button>
          </div>
        </div>
      </div>
    </>
  )
}